import { useState, useEffect } from 'react';
import { Plus, Search, Pencil, Eye, Trash2, ChevronLeft, ChevronRight, MoreVertical, X, Upload } from 'lucide-react';
import AdminShell from '../components/AdminShell';
import { useApp } from '../lib/AppContext';
import { useAuth } from '../lib/AuthContext';
import {
  listSongs,
  updateSong,
  deleteSong as deleteSongFromDB,
  publishSong,
  SongListItem
} from '../lib/supabase/songs';

export default function AdminSongs() {
  const { navigateTo, setCurrentCardId, showToast, refreshSongs } = useApp();
  const { user } = useAuth();
  const [songs, setSongs] = useState<SongListItem[]>([]);
  const [totalSongs, setTotalSongs] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'draft' | 'published' | 'archived'>('all');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const pageSize = 50;

  // Delete confirmation state
  const [deleteStep, setDeleteStep] = useState<0 | 1 | 2>(0);
  const [deletingSong, setDeletingSong] = useState<SongListItem | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Mobile action sheet state
  const [mobileActionSong, setMobileActionSong] = useState<SongListItem | null>(null);
  const [showMobileActions, setShowMobileActions] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchOffset, setTouchOffset] = useState(0);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    loadSongs();
  }, [filterStatus, debouncedSearch, page]);

  async function loadSongs() {
    setLoading(true);
    try {
      const { data, error } = await listSongs({
        status: filterStatus === 'all' ? undefined : filterStatus as any,
        search: debouncedSearch || undefined,
        includeDrafts: true,
      });

      if (error) {
        console.error('Failed to load songs:', error);
        showToast('Failed to load songs');
        setSongs([]);
        setTotalSongs(0);
        return;
      }

      setSongs(data || []);
      setTotalSongs((data || []).length);
    } catch (err) {
      console.error('Failed to load songs:', err);
      showToast('Failed to load songs');
      setSongs([]);
      setTotalSongs(0);
    } finally {
      setLoading(false);
    }
  }

  const filteredSongs = songs;
  const displayStart = (page - 1) * pageSize;
  const displayEnd = Math.min(displayStart + pageSize, totalSongs);
  const paginatedSongs = filteredSongs.slice(displayStart, displayEnd);
  const totalPages = Math.ceil(totalSongs / pageSize);

  function handleEdit(songId: string) {
    navigateTo('song-editor', songId);
  }

  function handlePreview(songId: string) {
    navigateTo('card-detail', songId);
  }

  async function handleTogglePublish(song: SongListItem) {
    if (!user) {
      showToast('You must be logged in');
      return;
    }

    const newStatus = song.status === 'published' ? 'draft' : 'published';
    const action = newStatus === 'published' ? 'publish' : 'unpublish';

    if (!confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} "${song.title}"?`)) return;

    try {
      if (newStatus === 'published') {
        const { error } = await publishSong(song.id, user.id);
        if (error) throw error;
      } else {
        const { error } = await updateSong(song.id, { status: 'draft' }, user.id);
        if (error) throw error;
      }

      showToast(`Song ${action}ed successfully`);
      loadSongs();
      refreshSongs();
    } catch (err) {
      console.error(`Failed to ${action} song:`, err);
      showToast(`Failed to ${action} song`);
    }
  }

  function handleDeleteClick(song: SongListItem) {
    setDeletingSong(song);
    setDeleteStep(1);
    setDeleteConfirmText('');
  }

  function closeDeleteModal() {
    setDeleteStep(0);
    setDeletingSong(null);
    setDeleteConfirmText('');
  }

  function handleDeleteStep1Continue() {
    setDeleteStep(2);
    setDeleteConfirmText('');
  }

  async function handleDeleteFinal() {
    if (!deletingSong) return;

    if (deleteConfirmText.trim().toUpperCase() !== 'DELETE') {
      showToast('Please type DELETE to confirm');
      return;
    }

    try {
      const { error } = await deleteSongFromDB(deletingSong.id);
      if (error) throw error;

      showToast('Song deleted successfully');
      closeDeleteModal();
      loadSongs();
      refreshSongs();
    } catch (err) {
      console.error('Failed to delete song:', err);
      showToast('Failed to delete song');
    }
  }

  function handleCreateNew() {
    navigateTo('song-editor');
  }

  function handleOpenMobileActions(song: SongListItem) {
    setMobileActionSong(song);
    setShowMobileActions(true);
  }

  function handleCloseMobileActions() {
    setShowMobileActions(false);
    setTimeout(() => setMobileActionSong(null), 300);
  }

  function handleMobilePreview() {
    if (!mobileActionSong) return;
    handlePreview(mobileActionSong.id);
    handleCloseMobileActions();
  }

  function handleMobileEdit() {
    if (!mobileActionSong) return;
    handleEdit(mobileActionSong.id);
    handleCloseMobileActions();
  }

  async function handleMobileTogglePublish() {
    if (!mobileActionSong) return;
    handleCloseMobileActions();
    await handleTogglePublish(mobileActionSong);
  }

  function handleMobileDelete() {
    if (!mobileActionSong) return;
    handleCloseMobileActions();
    handleDeleteClick(mobileActionSong);
  }

  function handleTouchStart(e: React.TouchEvent) {
    setTouchStart(e.touches[0].clientY);
    setTouchOffset(0);
  }

  function handleTouchMove(e: React.TouchEvent) {
    if (touchStart === null) return;
    const currentTouch = e.touches[0].clientY;
    const offset = currentTouch - touchStart;
    if (offset > 0) {
      setTouchOffset(offset);
    }
  }

  function handleTouchEnd() {
    if (touchOffset > 100) {
      handleCloseMobileActions();
    }
    setTouchStart(null);
    setTouchOffset(0);
  }

  return (
    <AdminShell activeSection="songs">
      <div className="p-4 sm:p-6 lg:p-8 w-full max-w-[1120px]">
        <div className="flex flex-col sm:flex-row items-start justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-[24px] sm:text-[28px] font-semibold text-[#F5F5F5] mb-2">Songs</h1>
            <p className="text-[13px] sm:text-[14px] text-[#A0A0A0]">
              <span className="hidden sm:inline">Manage all civic songs across the platform • </span>Total: <span className="text-[#40E0D0] font-semibold">{totalSongs}</span> songs
            </p>
          </div>
          <button
            onClick={handleCreateNew}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 bg-[#40E0D0] text-[#0A0A0A] rounded-lg font-semibold text-[14px] transition-all shadow-lg hover:brightness-105"
          >
            <Plus className="w-4 h-4" strokeWidth={2.5} />
            Create New Song
          </button>
        </div>


        <div className="mb-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#808080]" />
            <input
              type="text"
              placeholder="Search by title or artist..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-[#1A1A1A] border border-[#282828] rounded-lg text-[14px] text-[#F5F5F5] placeholder-[#808080] focus:outline-none focus:border-[#40E0D0]/50"
            />
          </div>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as any)}
            className="w-full sm:w-auto pl-4 pr-12 py-2.5 bg-[#1A1A1A] border border-[#282828] rounded-lg text-[14px] text-[#F5F5F5] focus:outline-none focus:border-[#40E0D0]/50 appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23A0A0A0%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%2F%3E%3C%2Fsvg%3E')] bg-[length:16px_16px] bg-[right_0.75rem_center] bg-no-repeat"
          >
            <option value="all">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </div>

        {loading ? (
          <div className="text-center py-12 text-[#808080]">Loading songs...</div>
        ) : paginatedSongs.length === 0 ? (
          <div className="bg-[#1A1A1A] border border-[#282828] rounded-xl p-12 text-center">
            <div className="w-16 h-16 bg-[#282828] rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-[#808080]" />
            </div>
            <h3 className="text-[16px] font-medium text-[#F5F5F5] mb-2">
              {totalSongs === 0 ? 'No songs in database yet' : 'No songs found'}
            </h3>
            <p className="text-[14px] text-[#A0A0A0] mb-6">
              {totalSongs === 0
                ? 'Get started by creating your first song'
                : searchQuery || filterStatus !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Get started by creating your first song'}
            </p>
            {(!searchQuery && filterStatus === 'all') && (
              <button
                onClick={handleCreateNew}
                className="px-5 py-2.5 bg-[#40E0D0] text-[#0A0A0A] rounded-lg font-semibold text-[14px] transition-all hover:brightness-105"
              >
                Create New Song
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden lg:block bg-[#1A1A1A] border border-[#282828] rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#0A0A0A] border-b border-[#282828]">
                  <tr>
                    <th className="text-left px-6 py-4 text-[12px] font-semibold text-[#A0A0A0] uppercase tracking-wider">
                      Song Title
                    </th>
                    <th className="text-left px-6 py-4 text-[12px] font-semibold text-[#A0A0A0] uppercase tracking-wider">
                      Artist
                    </th>
                    <th className="text-left px-6 py-4 text-[12px] font-semibold text-[#A0A0A0] uppercase tracking-wider">
                      Status
                    </th>
                    <th className="text-left px-6 py-4 text-[12px] font-semibold text-[#A0A0A0] uppercase tracking-wider">
                      Last Updated
                    </th>
                    <th className="text-right px-6 py-4 text-[12px] font-semibold text-[#A0A0A0] uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#282828]">
                  {paginatedSongs.map((song) => (
                    <tr
                      key={song.id}
                      className="group hover:bg-[#1F1F1F] transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="text-[14px] font-medium text-[#F5F5F5]">
                          {song.title}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-[14px] text-[#A0A0A0]">{song.artist}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-[12px] font-medium ${
                            song.status === 'published'
                              ? 'bg-[#52E3C2]/10 text-[#52E3C2]'
                              : song.status === 'archived'
                              ? 'bg-[#808080]/10 text-[#808080]'
                              : 'bg-[#FFB86C]/10 text-[#FFB86C]'
                          }`}
                        >
                          {song.status === 'published' && <Eye className="w-3 h-3 mr-1" />}
                          {song.status.charAt(0).toUpperCase() + song.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-[14px] text-[#A0A0A0]">
                        {song.updated_at ? new Date(song.updated_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        }) : '—'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => handleEdit(song.id)}
                            className="p-2 rounded-lg transition-colors hover:bg-[#282828]"
                            title="Edit"
                          >
                            <Pencil className="w-4 h-4 text-[#40E0D0]" />
                          </button>
                          <button
                            onClick={() => handlePreview(song.id)}
                            className="p-2 rounded-lg transition-colors hover:bg-[#282828]"
                            title="Preview"
                          >
                            <Eye className="w-4 h-4 text-[#A0A0A0]" />
                          </button>
                          <button
                            onClick={() => handleTogglePublish(song)}
                            className={`px-3 py-1.5 rounded-lg text-[12px] font-medium transition-colors ${
                              song.status === 'published'
                                ? 'bg-[#52E3C2]/10 text-[#52E3C2] hover:bg-[#52E3C2]/20'
                                : 'bg-[#FFB86C]/10 text-[#FFB86C] hover:bg-[#FFB86C]/20'
                            }`}
                            title={song.status === 'published' ? 'Unpublish' : 'Publish'}
                          >
                            {song.status === 'published' ? 'Unpublish' : 'Publish'}
                          </button>
                          <button
                            onClick={() => handleDeleteClick(song)}
                            className="p-2 rounded-lg transition-colors hover:bg-[#FF6F61]/10"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-[#FF6F61]" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-3">
              {paginatedSongs.map((song) => (
                <div
                  key={song.id}
                  className="bg-[#1A1A1A] border border-[#282828] rounded-xl p-4"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0 pr-3">
                      <h3 className="text-[15px] font-semibold text-[#F5F5F5] mb-1 truncate">
                        {song.title}
                      </h3>
                      <p className="text-[13px] text-[#A0A0A0] truncate">
                        {song.artist}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium ${
                          song.status === 'published'
                            ? 'bg-[#52E3C2]/10 text-[#52E3C2]'
                            : song.status === 'archived'
                            ? 'bg-[#808080]/10 text-[#808080]'
                            : 'bg-[#FFB86C]/10 text-[#FFB86C]'
                        }`}
                      >
                        {song.status === 'published' && <Eye className="w-3 h-3 mr-1" />}
                        {song.status.charAt(0).toUpperCase() + song.status.slice(1)}
                      </span>
                      <button
                        onClick={() => handleOpenMobileActions(song)}
                        className="p-2 rounded-lg transition-colors hover:bg-[#282828] active:bg-[#282828]"
                        aria-label="More actions"
                      >
                        <MoreVertical className="w-4 h-4 text-[#A0A0A0]" />
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={() => handleEdit(song.id)}
                    className="hidden md:flex w-full px-4 py-2.5 bg-[#40E0D0] text-[#0A0A0A] rounded-lg font-semibold text-[14px] transition-all hover:brightness-105 active:brightness-95 items-center justify-center gap-2"
                  >
                    <Pencil className="w-4 h-4" strokeWidth={2.5} />
                    Edit Song
                  </button>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="text-[13px] text-[#A0A0A0] text-center sm:text-left">
                  Showing {displayStart + 1}-{displayEnd} of {totalSongs} songs
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1.5 bg-[#1A1A1A] border border-[#282828] rounded-lg text-[13px] text-[#A0A0A0] hover:text-[#F5F5F5] hover:border-[#40E0D0]/30 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-[13px] text-[#A0A0A0]">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1.5 bg-[#1A1A1A] border border-[#282828] rounded-lg text-[13px] text-[#A0A0A0] hover:text-[#F5F5F5] hover:border-[#40E0D0]/30 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal - Step 1 */}
      {deleteStep === 1 && deletingSong && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-[#1A1A1A] border border-[#282828] rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
            <h2 className="text-[20px] font-semibold text-[#F5F5F5] mb-4">
              Delete this song?
            </h2>
            <p className="text-[14px] text-[#A0A0A0] mb-6 leading-relaxed">
              You're about to permanently delete <span className="text-[#F5F5F5] font-medium">"{deletingSong.title}"</span> by {deletingSong.artist}.
              This action cannot be undone and will remove all associated data.
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={closeDeleteModal}
                className="flex-1 px-4 py-2.5 bg-[#282828] text-[#F5F5F5] rounded-lg font-medium text-[14px] transition-all hover:brightness-110"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteStep1Continue}
                className="flex-1 px-4 py-2.5 bg-[#FF6F61] text-white rounded-lg font-medium text-[14px] transition-all hover:brightness-110"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal - Step 2 */}
      {deleteStep === 2 && deletingSong && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-[#1A1A1A] border border-[#282828] rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
            <h2 className="text-[20px] font-semibold text-[#F5F5F5] mb-4">
              Confirm deletion
            </h2>
            <p className="text-[14px] text-[#A0A0A0] mb-4 leading-relaxed">
              Type <span className="text-[#F5F5F5] font-mono font-semibold">DELETE</span> to permanently remove this song:
            </p>
            <p className="text-[14px] text-[#F5F5F5] font-medium mb-6 px-4 py-3 bg-[#0A0A0A] rounded-lg border border-[#282828]">
              {deletingSong.title} — {deletingSong.artist}
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Type DELETE"
              className="w-full px-4 py-2.5 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[14px] text-[#F5F5F5] placeholder-[#808080] focus:outline-none focus:border-[#FF6F61]/50 mb-6"
              autoFocus
            />
            <div className="flex items-center gap-3">
              <button
                onClick={closeDeleteModal}
                className="flex-1 px-4 py-2.5 bg-[#282828] text-[#F5F5F5] rounded-lg font-medium text-[14px] transition-all hover:brightness-110"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteFinal}
                disabled={deleteConfirmText.trim().toUpperCase() !== 'DELETE'}
                className="flex-1 px-4 py-2.5 bg-[#FF6F61] text-white rounded-lg font-medium text-[14px] transition-all hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:brightness-100"
              >
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Action Sheet - Bottom Sheet (Mobile Only) */}
      {showMobileActions && mobileActionSong && (
        <div className="lg:hidden fixed inset-0 z-50" onClick={handleCloseMobileActions}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className="absolute bottom-0 left-0 right-0 bg-[#1A1A1A] border-t border-[#282828] rounded-t-3xl shadow-2xl"
            style={{
              transform: `translateY(${touchOffset}px)`,
              transition: touchStart === null ? 'transform 0.3s ease-out' : 'none'
            }}
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Swipe indicator */}
            <div className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing">
              <div className="w-12 h-1 bg-[#404040] rounded-full" />
            </div>

            {/* Song info */}
            <div className="px-6 pt-3 pb-4 border-b border-[#282828]">
              <h3 className="text-[15px] font-semibold text-[#F5F5F5] mb-1">
                {mobileActionSong.title}
              </h3>
              <p className="text-[13px] text-[#A0A0A0]">
                {mobileActionSong.artist}
              </p>
            </div>

            {/* Actions */}
            <div className="px-4 py-3">
              <button
                onClick={handleMobilePreview}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-lg transition-colors hover:bg-[#282828] active:bg-[#282828] text-left"
              >
                <Eye className="w-5 h-5 text-[#A0A0A0]" />
                <span className="text-[15px] text-[#F5F5F5]">Preview</span>
              </button>

              <button
                onClick={handleMobileEdit}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-lg transition-colors hover:bg-[#282828] active:bg-[#282828] text-left"
              >
                <Pencil className="w-5 h-5 text-[#40E0D0]" />
                <span className="text-[15px] text-[#F5F5F5]">Edit Song</span>
              </button>

              <button
                onClick={handleMobileTogglePublish}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-lg transition-colors hover:bg-[#282828] active:bg-[#282828] text-left"
              >
                {mobileActionSong.status === 'published' ? (
                  <>
                    <Upload className="w-5 h-5 text-[#FFB86C] rotate-180" />
                    <span className="text-[15px] text-[#F5F5F5]">Unpublish</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5 text-[#52E3C2]" />
                    <span className="text-[15px] text-[#F5F5F5]">Publish</span>
                  </>
                )}
              </button>

              {/* Separator before destructive action */}
              <div className="h-px bg-[#282828] my-2" />

              <button
                onClick={handleMobileDelete}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-lg transition-colors hover:bg-[#FF6F61]/10 active:bg-[#FF6F61]/10 text-left"
              >
                <Trash2 className="w-5 h-5 text-[#FF6F61]" />
                <span className="text-[15px] text-[#FF6F61]">Delete Song</span>
              </button>
            </div>

            {/* Cancel button */}
            <div className="px-4 pb-6 pt-2">
              <button
                onClick={handleCloseMobileActions}
                className="w-full px-4 py-3 bg-[#282828] text-[#F5F5F5] rounded-lg font-medium text-[15px] transition-all hover:brightness-110 active:brightness-95"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
